import React, { useState, useEffect, useMemo } from "react";
import { Layout, Menu } from "antd";
import { Link, useLocation } from "react-router-dom";
import { sidebarItems } from "./SidebarData";
import { colors } from "../../../constants/theme";
import { useGetMeQuery } from "../../../redux/api/Admin/AuthApi";
import "./Sidebar.css";

const { Sider } = Layout;
const { SubMenu } = Menu;

const Sidebar = () => {
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);

  // 🔐 Get current user
  const { data: userData, isLoading } = useGetMeQuery();
  const currentUser = userData?.data?.user;

  // 🔍 Find selected menu
  const findSelectedKey = (items, path) => {
    for (const item of items) {
      if (item.url === path) return item.id.toString();
      if (item.children) {
        const childKey = findSelectedKey(item.children, path);
        if (childKey) return childKey;
      }
    }
    return null;
  };

  // 🔐 Filter sidebar based on role
  const filteredSidebarItems = useMemo(() => {
    if (!currentUser) return [];
    return sidebarItems.filter((item) => item.roles.includes(currentUser.role));
  }, [currentUser]);

  const selectedKey = findSelectedKey(filteredSidebarItems, location.pathname);

  // Auto-open parent menu
  useEffect(() => {
    const findParentKey = (items, path) => {
      for (const item of items) {
        if (item.url === path) return item.id.toString();
        if (item.children) {
          const childKey = findParentKey(item.children, path);
          if (childKey) return item.id.toString();
        }
      }
      return null;
    };

    const parentKey = findParentKey(filteredSidebarItems, location.pathname);
    if (parentKey) setOpenKeys([parentKey]);
  }, [location.pathname, filteredSidebarItems]);

  const onOpenChange = (keys) => setOpenKeys(keys);

  if (isLoading || !currentUser) return null;

  return (
    <Sider
      collapsible={false}
      trigger={null}
      width={200}
      className="site-layout-background d-none d-lg-block custom-sidebar"
      style={{
        borderRight: `1px solid ${colors.border}`,
        position: "fixed",
        height: "100vh",
        overflowY: "auto",
        background: colors.bgWhite,
        boxShadow: colors.shadowLight,
      }}
    >
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        style={{
          height: "100%",
          borderRight: 0,
          padding: "12px 8px",
        }}
        theme="light"
      >
        {filteredSidebarItems.map((menu) =>
          menu.children && menu.children.length > 0 ? (
            <SubMenu
              key={menu.id}
              icon={menu.icon}
              title={<span style={{ fontWeight: 500 }}>{menu.label}</span>}
            >
              {menu.children.map((item) => (
                <Menu.Item key={item.id} icon={item.icon}>
                  <Link className="link-unstyled" to={item.url}>
                    {item.label}
                  </Link>
                </Menu.Item>
              ))}
            </SubMenu>
          ) : (
            <Menu.Item key={menu.id} icon={menu.icon}>
              <Link className="link-unstyled" to={menu.url}>
                {menu.label}
              </Link>
            </Menu.Item>
          )
        )}
      </Menu>
    </Sider>
  );
};

export default Sidebar;
